import { Inject, Injectable, NgZone } from '@angular/core';
import { Collections } from '@freelancer/datastore/collections.model';
import { enterZone, leaveZone } from '@freelancer/time-utils';
import { Actions, Effect } from '@ngrx/effects';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  mergeAll,
  mergeMap,
  observeOn,
  publishReplay,
  refCount,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  isRequestDataAction,
  RequestDataPayload,
  TypedAction,
} from '../actions';
import { ApiFetchResponse } from '../api-http.service';
import { StoreBackend } from '../backend';
import { chunk, flatMap } from '../helpers';
import { slidingBufferTime } from '../operators/slidingBufferTime';
import { RequestStatusHandler } from '../request-status-handler.service';
import { referencesEqual } from '../store.helpers';
import { batchRequests } from './operators/batchRequests';
import { dedupeRequests } from './operators/dedupeRequests';
import { REQUEST_DATA_CONFIG } from './request-data.config';
import { RequestDataConfig } from './request-data.interface';

@Injectable()
export class RequestDataEffect {
  @Effect() readonly requestData$: Rx.Observable<TypedAction>;

  constructor(
    private storeBackend: StoreBackend,
    private actions$: Actions<TypedAction>,
    private ngZone: NgZone,
    private requestStatusHandler: RequestStatusHandler,
    @Inject(REQUEST_DATA_CONFIG) private config: RequestDataConfig,
  ) {
    const { scheduler, dedupeWindowTime, batchWindowTime } = this.config;

    // Optimisation: dedupe requests with limit smaller than the largest in window
    const allRequests$ = this.actions$.pipe(
      filter(isRequestDataAction),
      map(action => action.payload),
    );
    const requestErrors$ = this.requestStatusHandler.statusStream$.pipe(
      filter(({ statusObject }) => statusObject.error !== undefined),
    );

    const allDedupedRequests$ = Rx.combineLatest(
      allRequests$.pipe(
        dedupeRequests(
          dedupeWindowTime,
          requestErrors$.pipe(map(e => e.request)),
          leaveZone(this.ngZone, scheduler),
        ),
      ),
      requestErrors$.pipe(
        withLatestFrom(
          Rx.combineLatest(
            // sliding buffer of failed requests clientRequestIds
            requestErrors$.pipe(
              map(({ request }) => request),
              slidingBufferTime(
                dedupeWindowTime,
                // this is a bit of a hack, but not leaving the NgZone when the
                // dedupeWindowTime is null enables the unit tests to work
                dedupeWindowTime
                  ? leaveZone(this.ngZone, scheduler)
                  : undefined,
              ),
              map(requestIdErrorBuffer =>
                flatMap(
                  requestIdErrorBuffer,
                  ({ clientRequestIds }) => clientRequestIds,
                ),
              ),
            ),
            // sliding buffer of all requests
            allRequests$.pipe(
              slidingBufferTime(
                dedupeWindowTime,
                // this is a bit of a hack, but not leaving the NgZone when the
                // dedupeWindowTime is null enables the unit tests to work
                dedupeWindowTime
                  ? leaveZone(this.ngZone, scheduler)
                  : undefined,
              ),
            ),
          ),
        ),
        tap(
          ([
            { request, statusObject },
            [requestIdErrorBuffer, requestBuffer],
          ]) =>
            requestBuffer
              .filter(
                r =>
                  // has a previous request (r) been made to the same ref
                  referencesEqual(r.ref, request.ref) &&
                  // has none of the current request (request) clientRequestIds resulted in a previous error
                  !r.clientRequestIds.some(id =>
                    requestIdErrorBuffer.includes(id),
                  ),
              )
              .forEach(r => this.requestStatusHandler.update(r, statusObject)),
        ),
        mapTo(undefined),
        startWith<undefined>(undefined),
      ),
    ).pipe(
      map(([source]) => source),
      distinctUntilChanged(),
      publishReplay(1),
      refCount(),
    );

    const plainRequests$ = allDedupedRequests$.pipe(
      filter(this.isPlainRequest),
    );
    const queriedRequests$ = allDedupedRequests$.pipe(
      filter(request => !this.isPlainRequest(request)),
    );

    // Batching only for plain requests (no query)
    // TODO: Address queried requests, i.e. regular datastore.list() calls
    this.requestData$ = Rx.merge(
      plainRequests$.pipe(
        batchRequests(batchWindowTime, leaveZone(this.ngZone, scheduler)),
        map(requests =>
          flatMap(requests, request =>
            splitRequestsOnId(
              request,
              this.storeBackend.batchSize(request.ref),
            ),
          ),
        ),
        mergeAll(),
        observeOn(enterZone(this.ngZone, scheduler)),
      ),
      queriedRequests$,
    ).pipe(
      mergeMap(request =>
        this.storeBackend
          .fetch(request.ref)
          .pipe(map(res => ({ res, request }))),
      ),
      map(({ res, request }) => this.onResponse(res, request) as TypedAction),
      filter(action => action !== undefined),
    );
  }

  private onResponse(
    response: ApiFetchResponse<any>,
    request: RequestDataPayload<any>,
  ): TypedAction | undefined {
    const order =
      request.ref.order ||
      this.storeBackend.defaultOrder(request.ref.path.collection);
    switch (response.status) {
      case 'success': {
        const action: TypedAction = {
          type: 'API_FETCH_SUCCESS',
          payload: {
            type: request.ref.path.collection,
            result: response.result,
            ref: request.ref,
            order,
          },
        };
        return action;
      }
      default: {
        this.requestStatusHandler.update(request, {
          ready: false,
          error: response,
        });
        const action: TypedAction = {
          type: 'API_FETCH_ERROR',
          payload: {
            type: request.ref.path.collection,
            ref: request.ref,
            order,
          },
        };
        return action;
      }
    }
  }

  private isPlainRequest(request: RequestDataPayload<any>) {
    return request.ref.query === undefined;
  }
}

function splitRequestsOnId<K extends keyof Collections>(
  request: RequestDataPayload<K>,
  size: number,
): ReadonlyArray<RequestDataPayload<K>> {
  const { ids } = request.ref.path;
  if (!ids) {
    return [request];
  }

  return chunk(ids, size).map(chunkedIds => ({
    ...request,
    ref: {
      ...request.ref,
      path: {
        ...request.ref.path,
        ids: chunkedIds,
      },
    },
  }));
}
